import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { PUBLICACOES_SLOTS } from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_publicacoes_block',
    defaultMessage: 'Publicações e Editais (capa)',
  },
  aba: { id: 'mpf_publicacoes_aba', defaultMessage: 'Aba {slot}' },
  abaLabel: {
    id: 'mpf_publicacoes_aba_label',
    defaultMessage: 'Rótulo da aba',
  },
  abaLabelHelp: {
    id: 'mpf_publicacoes_aba_label_help',
    defaultMessage: 'Texto da pílula. Aba sem rótulo e sem links não aparece.',
  },
  abaLinks: { id: 'mpf_publicacoes_aba_links', defaultMessage: 'Links' },
  link: { id: 'mpf_publicacoes_link', defaultMessage: 'Link' },
  linkTitle: { id: 'mpf_publicacoes_link_title', defaultMessage: 'Título' },
  linkTitleHelp: {
    id: 'mpf_publicacoes_link_title_help',
    defaultMessage:
      'Até 60 caracteres é o que costuma caber numa linha da lista sem quebrar. Acima disso o título ainda aparece inteiro.',
  },
  linkHref: { id: 'mpf_publicacoes_link_href', defaultMessage: 'Destino' },
  linkHrefHelp: {
    id: 'mpf_publicacoes_link_href_help',
    defaultMessage:
      'Escolha uma página do site ou cole um endereço. Ao escolher uma página, o título é preenchido com o título dela — título já editado não é sobrescrito.',
  },
  linkExternal: {
    id: 'mpf_publicacoes_link_external',
    defaultMessage: 'Abrir em nova aba',
  },
});

/** Campos de um link do painel. */
const linkSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.link),
  addMessage: intl.formatMessage(messages.link),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'href', 'external'],
    },
  ],
  properties: {
    // Contador em vez de corte: o link ainda funciona com um título longo,
    // é decisão editorial com aviso na hora de escrever. Ver
    // `widgets/CharCountWidget`.
    title: {
      title: intl.formatMessage(messages.linkTitle),
      description: intl.formatMessage(messages.linkTitleHelp),
      widget: 'charCount',
      recommendedLength: SEO_TITLE_LENGTH,
    },
    href: {
      title: intl.formatMessage(messages.linkHref),
      description: intl.formatMessage(messages.linkHrefHelp),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['@id', '@type', 'Title'],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.linkExternal),
      type: 'boolean',
    },
  },
  required: ['title'],
});

/** Ids dos campos de um slot de aba. Fonte única do nome dos campos. */
export const abaFieldIds = (slot: number) => ({
  label: `aba${slot}Label`,
  links: `aba${slot}Links`,
});

/** Ids dos quatro campos `object_list`, na ordem dos slots. */
export const PUBLICACOES_LINK_FIELDS = PUBLICACOES_SLOTS.map(
  (slot) => abaFieldIds(slot).links,
);

/**
 * Id do fieldset de cada slot.
 *
 * O `InlineForm` do Volto acessa `fieldsets.find(o => o.id === 'default').id`
 * sem guarda — um schema sem fieldset `default` derruba a sidebar inteira. O
 * cabeçalho desta faixa é todo fixo no componente (sem campo de título
 * editável), então não sobra campo avulso para abrir o `default`: a Aba 1 é
 * quem mora nele, e as demais viram `aba2`/`aba3`/`aba4`. Exportado porque o
 * `Edit.tsx` usa o mesmo id para rolar a sidebar até a aba certa.
 */
export const fieldsetIdForSlot = (slot: number): string =>
  slot === 1 ? 'default' : `aba${slot}`;

/**
 * Schema do bloco "Publicações e Editais".
 *
 * As quatro abas são slots FIXOS, gravadas em campos achatados (`aba1Label`,
 * `aba1Links`, ...) e não numa lista de abas: um `object_list` dentro de
 * outro não é suportado pelo Volto — ver `OndeAtua/schema.ts`.
 *
 * `activeObject`/`setActiveObject` são por slot, mesmo mecanismo do
 * `mpfOndeAtuaSchema`: `activeObjects[slot]` traz o índice aberto naquela
 * lista e `-1` nas demais.
 */
export const mpfPublicacoesEditaisSchema = ({
  intl,
  activeObjects = {},
  setActiveObject,
}: {
  intl: IntlShape;
  activeObjects?: Record<number, number>;
  setActiveObject?: (slot: number, index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: PUBLICACOES_SLOTS.map((slot) => ({
    id: fieldsetIdForSlot(slot),
    title: intl.formatMessage(messages.aba, { slot }),
    fields: Object.values(abaFieldIds(slot)),
  })),
  properties: Object.fromEntries(
    PUBLICACOES_SLOTS.flatMap((slot) => {
      const ids = abaFieldIds(slot);
      return [
        [
          ids.label,
          {
            title: intl.formatMessage(messages.abaLabel),
            description: intl.formatMessage(messages.abaLabelHelp),
          },
        ],
        [
          ids.links,
          {
            title: intl.formatMessage(messages.abaLinks),
            widget: 'object_list',
            schema: linkSchema({ intl }),
            activeObject: activeObjects[slot] ?? -1,
            setActiveObject: setActiveObject
              ? (index: number) => setActiveObject(slot, index)
              : undefined,
          },
        ],
      ];
    }),
  ),
  required: [],
});

export default mpfPublicacoesEditaisSchema;
